import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { useAuth } from '@/lib/auth';
import { normalizeProfileTrialData } from '@/lib/trialUtils';
import { logger } from '@/lib/logger';
import type { WorkspaceInfo } from '@/contexts/WorkspaceScope.context';

interface Workspace {
  id: string;
  name: string;
  industry: string | null;
  timezone?: string;
  currency?: string;
  created_at: string;
}

interface Profile {
  id: string;
  workspace_id: string | null;
  name: string;
  email: string;
  account_status: string | null;
  trial_start_date: string | null;
  trial_end_date: string | null;
}

interface UserRole {
  role: 'admin' | 'sales' | 'marketing';
}

/**
 * Simplified useWorkspace hook that uses centralized WorkspaceScopeContext.
 * All workspace data is scoped to activeWorkspaceId from context.
 */
export function useWorkspace() {
  const { user } = useAuth();
  const { activeWorkspaceId, workspaces, isLoading: workspacesLoading } = useWorkspaceScope();

  // Get workspace data from context (already fetched via get_my_workspaces RPC)
  const workspace = workspaces.find(w => w.workspace_id === activeWorkspaceId);
  
  // Convert WorkspaceMember to Workspace format
  const workspaceData: Workspace | null = workspace ? {
    id: workspace.workspace_id,
    name: workspace.workspace_name,
    industry: null,
    created_at: workspace.created_at,
  } : null;

  // Fetch profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async (): Promise<Profile | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, name, workspace_id, account_status, trial_start_date, trial_end_date, is_internal')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        logger.error('[useWorkspace] Error fetching profile', error, { userId: user.id });
        return null;
      }

      if (!data) return null;
      const normalized = normalizeProfileTrialData(data);
      return {
        ...data,
        ...normalized,
      } as Profile;
    },
    enabled: !!user?.id,
  });

  // Fetch user role
  const { data: role, isLoading: roleLoading } = useQuery({
    queryKey: ['user-role', activeWorkspaceId, user?.id],
    queryFn: async (): Promise<UserRole['role'] | null> => {
      if (!activeWorkspaceId || !user?.id) return null;

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('workspace_id', activeWorkspaceId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        logger.error('[useWorkspace] Error fetching role', error, { workspaceId: activeWorkspaceId, userId: user?.id });
        return null;
      }

      return data?.role || null;
    },
    enabled: !!activeWorkspaceId && !!user?.id,
  });

  return {
    workspace: workspaceData,
    profile,
    role,
    loading: workspacesLoading || profileLoading || roleLoading,
    needsWorkspace: !activeWorkspaceId,
    error: null, // Errors are handled by useWorkspaceInit
  };
}

